import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type { OpenTaskParams } from "./tasks.context";

export const TASK_ID_PARAM = "taskId";
const TASK_WORKSPACE_ID_PARAM = "taskWorkspaceId";
const TASK_PANEL_PARAM = "taskPanel";
const OPENED_IN_PAGE_STATE_KEY = "isTaskOpenedInPage";

const withoutTask = (search: string) => {
    const params = new URLSearchParams(search);
    params.delete(TASK_ID_PARAM);
    params.delete(TASK_WORKSPACE_ID_PARAM);
    params.delete(TASK_PANEL_PARAM);
    return params;
};

const withTask = ({
    search,
    taskId,
    workspaceId,
}: OpenTaskParams & { search: string }) => {
    const params = withoutTask(search);
    params.set(TASK_ID_PARAM, taskId);
    if (workspaceId) {
        params.set(TASK_WORKSPACE_ID_PARAM, workspaceId);
    }
    return `?${params}`;
};

interface OpenTaskLocationState {
    [OPENED_IN_PAGE_STATE_KEY]?: boolean;
}

/**
 * The part of react-router's untyped `location.state` the open task keeps:
 * whether the page opened it. An in-place task link passes it on, so closing
 * the task it swaps in still goes back to the list entry.
 */
export const getOpenTaskLocationState = (
    state: OpenTaskLocationState | null,
): OpenTaskLocationState =>
    state?.[OPENED_IN_PAGE_STATE_KEY] === true
        ? { [OPENED_IN_PAGE_STATE_KEY]: true }
        : {};

/** The URL's open task; busy closes demo tasks never open, so they never reach the server. */
const getSelectedTaskId = (search: string) => {
    const taskId = new URLSearchParams(search).get(TASK_ID_PARAM);
    return isBusyClosesDemoId(taskId) ? null : taskId;
};

/**
 * The open task lives in the URL's `taskId`. Opening from the page pushes a
 * history entry (marked in location state), so closing it goes back to the
 * list entry and Forward reopens it; a task opened from a link closes by
 * dropping the params in place.
 *
 * The callbacks read the location when called, so they keep their identity
 * across URL changes and the tasks context doesn't re-render every list row.
 */
export const useOpenTaskUrl = () => {
    const location = useLocation();
    const locationRef = useLatestRef(location);
    const navigate = useStateAwareNavigate();
    const selectedTaskId = useMemo(
        () => getSelectedTaskId(location.search),
        [location.search],
    );

    const goToTask = useCallback(
        ({ taskId, workspaceId }: OpenTaskParams) => {
            const { pathname, search, state } = locationRef.current;
            navigate(
                {
                    pathname,
                    search: withTask({ search, taskId, workspaceId }),
                },
                { replace: true, state: getOpenTaskLocationState(state) },
            );
        },
        [locationRef, navigate],
    );

    const openTask = useCallback(
        (params: OpenTaskParams) => {
            const { pathname, search } = locationRef.current;
            if (getSelectedTaskId(search) !== null) {
                goToTask(params);
                return;
            }
            navigate(
                { pathname, search: withTask({ search, ...params }) },
                { state: { [OPENED_IN_PAGE_STATE_KEY]: true } },
            );
        },
        [goToTask, locationRef, navigate],
    );

    const closeTask = useCallback(() => {
        const { pathname, search, state } = locationRef.current;
        if (getOpenTaskLocationState(state)[OPENED_IN_PAGE_STATE_KEY]) {
            navigate(-1);
            return;
        }
        navigate(
            { pathname, search: `?${withoutTask(search)}` },
            { replace: true },
        );
    }, [locationRef, navigate]);

    return { selectedTaskId, openTask, goToTask, closeTask };
};
